import { useUser } from '@store';

/**
 * The Hasura role this session's queries run as. The portal sends no role
 * header, so it is the JWT's default role — and it is NOT always `admin`:
 * checkForPermittedRole admits dealer and dispatcher roles to this portal too,
 * and those run against their own (permissioned) schema.
 */
export const useHasuraRole = (): string | undefined => useUser()?.claims?.['x-hasura-default-role'];

/**
 * True only for the admin Hasura role, which bypasses table permissions. Gate
 * anything that reads a table with no role permissions (dispatchnotes) on this,
 * not on a feature permission — a dealer-admin with the DISPATCHING feature can
 * open the pages but can never validate a query that selects that table.
 */
export const useIsHasuraAdmin = (): boolean => useHasuraRole() === 'admin';

/**
 * Roles granted select + insert on dispatchnotes in Hasura metadata
 * (public_dispatchnotes.yaml). admin bypasses permissions; dispatcher holds an
 * explicit grant. Keep this list in step with that file.
 */
export const DISPATCH_NOTE_ROLES: readonly string[] = ['admin', 'dispatcher'];

/** True when this session can read and write dispatch notes on their own pages. */
export const useCanUseDispatchNotes = (): boolean => {
  const role = useHasuraRole();
  return role !== undefined && DISPATCH_NOTE_ROLES.includes(role);
};
